import { apiService } from './api';

export interface ContractTask {
  id: string;
  projectId: string;
  quoteId: string;
  ownerId: string;
  status: 'generating' | 'completed' | 'failed';
  progress: number;
  createdAt: string;
  estimatedCompletion?: string;
  errorMessage?: string;
}

export interface Contract {
  id: string;
  projectId: string;
  ownerId: string;
  quoteId: string;
  builderId: string;
  title: string;
  contractDate: string;
  sections: ContractSection[];
  generatedAt: string;
  version: string;
}

export interface ContractSection {
  number: string;
  title: string;
  clauses: ContractClause[];
}

export interface ContractClause {
  id: string;
  content: string;
}

class ContractService {
  // Generate contract for project
  async generateContract(projectId: string, quoteId: string): Promise<{ contractId: string; status: string }> {
    return apiService.post(`/projects/${projectId}/contract/generate`, { quoteId });
  }

  // Get contract generation status
  async getContractStatus(projectId: string, contractId: string): Promise<ContractTask> {
    return apiService.get(`/projects/${projectId}/contract/${contractId}/status`);
  }

  // Get contract document
  async getContract(projectId: string, contractId: string): Promise<Contract> {
    return apiService.get(`/projects/${projectId}/contract/${contractId}`);
  }

  // Poll for contract completion
  async pollContractStatus(
    projectId: string,
    contractId: string,
    onProgress?: (progress: number) => void,
    maxAttempts: number = 60,
    intervalMs: number = 5000
  ): Promise<Contract> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const status = await this.getContractStatus(projectId, contractId);

      if (onProgress) {
        onProgress(status.progress);
      }

      if (status.status === 'completed') {
        return await this.getContract(projectId, contractId);
      }

      if (status.status === 'failed') {
        throw new Error(status.errorMessage || 'Contract generation failed');
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    throw new Error('Contract generation timeout');
  }
}

export const contractService = new ContractService();
